import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Download,
  RefreshCw,
  RotateCw,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import type {
  DesktopApplication,
  DesktopUpdateState,
} from '@/auth/desktopAuth';
import { presentDesktopUpdate } from './desktopUpdatePresentation';

const toneClasses = {
  neutral: 'border-[var(--lex-border)] bg-[var(--lex-surface-card)]',
  positive: 'border-[var(--lex-status-success)] bg-[color-mix(in_oklab,var(--lex-status-success)_10%,var(--lex-surface-card))]',
  warning: 'border-[var(--lex-status-warning)] bg-[color-mix(in_oklab,var(--lex-status-warning)_10%,var(--lex-surface-card))]',
  danger: 'border-[var(--lex-status-danger)] bg-[color-mix(in_oklab,var(--lex-status-danger)_10%,var(--lex-surface-card))]',
} as const;

export default function UpdateCenterPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [state, setState] = useState<DesktopUpdateState | null>(null);
  const [applications, setApplications] = useState<DesktopApplication[]>([]);
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const bridge = window.edgelexDesktop;

  useEffect(() => {
    if (!bridge?.updates) return;
    let active = true;
    void Promise.all([
      bridge.updates.getState(),
      bridge.applications.list(),
    ]).then(([nextState, nextApplications]) => {
      if (!active) return;
      setState(nextState);
      setApplications(nextApplications);
    }).catch(() => {
      if (active) setActionError('EdgeLex could not read the desktop update state.');
    });
    const unsubscribe = bridge.updates.onStateChange((nextState) => {
      if (active) setState(nextState);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [bridge]);

  const presentation = useMemo(
    () => state ? presentDesktopUpdate(state) : null,
    [state],
  );

  const runAction = useCallback(async (action: 'check' | 'restart' | 'defer') => {
    if (!bridge?.updates) return;
    setActionPending(true);
    setActionError(null);
    try {
      const nextState = action === 'check'
        ? await bridge.updates.check()
        : action === 'restart'
          ? await bridge.updates.installAndRestart()
          : await bridge.updates.defer();
      setState(nextState);
    } catch {
      setActionError('The requested update action could not be completed. Your current EdgeLex installation was not changed.');
    } finally {
      setActionPending(false);
    }
  }, [bridge]);

  const statusIcon = state?.status === 'ready'
    ? <Download className="h-7 w-7" />
    : state?.status === 'up-to-date'
      ? <Check className="h-7 w-7" />
      : <RefreshCw className={`h-7 w-7 ${state?.status === 'checking' || state?.status === 'downloading' ? 'animate-spin' : ''}`} />;

  return (
    <main className="min-h-dvh bg-[var(--lex-surface-app)] px-5 py-8 text-[var(--lex-text-primary)] sm:px-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--lex-accent-primary)] text-[var(--lex-accent-contrast)]">
              <RotateCw className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">Update EdgeLex</h1>
              <p className="text-sm text-[var(--lex-text-secondary)]">Application, security, and desktop capability updates</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/chat')}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--lex-border)] px-4 py-2 text-sm font-medium hover:bg-[var(--lex-surface-hover)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Full EdgeLex
          </button>
        </header>

        {!bridge ? (
          <section className="rounded-xl border border-[var(--lex-border)] bg-[var(--lex-surface-card)] p-6">
            <h2 className="text-lg font-semibold">Desktop updates are available in the installed EdgeLex application</h2>
            <p className="mt-2 text-sm text-[var(--lex-text-secondary)]">This browser session continues to receive normal web deployments automatically.</p>
          </section>
        ) : !state || !presentation ? (
          <section className="rounded-xl border border-[var(--lex-border)] bg-[var(--lex-surface-card)] p-6 text-sm text-[var(--lex-text-secondary)]">
            Reading the installed EdgeLex version…
          </section>
        ) : (
          <>
            <section className={`rounded-2xl border p-6 ${toneClasses[presentation.tone]}`}>
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--lex-accent-primary)] text-[var(--lex-accent-contrast)]">
                    {statusIcon}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">{presentation.title}</h2>
                    <p className="mt-1 text-sm text-[var(--lex-text-secondary)]">{presentation.detail}</p>
                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[var(--lex-text-tertiary)]">
                      <span>Installed: {state.currentVersion}</span>
                      <span>Channel: {state.channel}</span>
                      {state.channel === 'pilot' && <span>Pilot enrollment: {state.channelSource}</span>}
                      {state.lastCheckedAt && <span>Checked: {new Date(state.lastCheckedAt).toLocaleString()}</span>}
                    </div>
                  </div>
                </div>
                {presentation.primaryAction && (
                  <button
                    type="button"
                    disabled={actionPending}
                    onClick={() => void runAction(presentation.primaryAction!)}
                    className="inline-flex min-w-40 items-center justify-center gap-2 rounded-lg bg-[var(--lex-accent-primary)] px-4 py-2.5 text-sm font-semibold text-[var(--lex-accent-contrast)] disabled:opacity-60"
                  >
                    {presentation.primaryAction === 'restart' ? <RotateCw className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
                    {presentation.primaryAction === 'restart' ? 'Restart to Update' : 'Check for Updates'}
                  </button>
                )}
              </div>
              {state.status === 'ready' && (
                <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-[var(--lex-border)] pt-4">
                  <button
                    type="button"
                    disabled={actionPending}
                    onClick={() => void runAction('defer')}
                    className="rounded-lg border border-[var(--lex-border)] px-4 py-2 text-sm hover:bg-[var(--lex-surface-hover)]"
                  >
                    Later
                  </button>
                  <span className="text-xs text-[var(--lex-text-tertiary)]">The downloaded update will also install the next time EdgeLex starts.</span>
                </div>
              )}
              {actionError && <p className="mt-4 text-sm text-[var(--lex-status-danger)]">{actionError}</p>}
            </section>

            {state.releaseNotes && (
              <section className="rounded-xl border border-[var(--lex-border)] bg-[var(--lex-surface-card)] p-6">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-[var(--lex-accent-primary)]" />
                  <h2 className="text-lg font-semibold">What’s new</h2>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-[var(--lex-text-secondary)]">{state.releaseNotes}</p>
              </section>
            )}

            <section className="rounded-xl border border-[var(--lex-border)] bg-[var(--lex-surface-card)] p-6">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-[var(--lex-accent-primary)]" />
                <h2 className="text-lg font-semibold">Applications included in EdgeLex Desktop</h2>
              </div>
              <p className="mt-1 text-sm text-[var(--lex-text-secondary)]">These applications ship from the same reviewed EdgeLex frontend build.</p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {applications.map((application) => (
                  <div key={application.id} className="rounded-lg border border-[var(--lex-border)] bg-[var(--lex-surface-app)] p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{application.label}</span>
                      <span className="rounded-full bg-[color-mix(in_oklab,var(--lex-status-success)_18%,transparent)] px-2 py-0.5 text-[10px] font-semibold text-[var(--lex-status-success)]">Included</span>
                    </div>
                    <p className="mt-1 text-xs text-[var(--lex-text-tertiary)]">{application.route}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
